import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-800 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${category.name}韩漫 - 好看的${category.name}韩国漫画 - 韩漫网站`}
        description={`韩漫网站提供精选${category.name}韩国漫画免费阅读，${category.description}。高品质韩漫在线阅读，舒适阅读体验。`}
        keywords={`${category.name},韩漫,韩国漫画,${category.name}漫画,免费阅读`}
        canonicalUrl={`https://www.manhwa-site.com/category/${slug}`}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />

          <div className="py-6">
            <div className="flex items-center gap-4 mb-2">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${category.icon} text-2xl text-accent-600`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">{category.name}韩国漫画</h1>
                <p className="mt-1 text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">暂无该分类的漫画，请查看其他分类</p>
            </div>
          )}
        </div>

        <section className="bg-background-100/60 py-14" aria-labelledby="other-categories">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="other-categories" className="text-xl font-bold text-foreground-800 font-heading mb-6">探索更多分类</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories.filter((c) => c.slug !== slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 rounded-lg bg-background-50 hover:bg-background-100 border border-background-200/70 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${cat.icon} text-sm text-accent-600`}></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          'name': `${category.name}韩漫 - 韩漫网站`,
          'description': category.description,
          'url': `https://www.manhwa-site.com/category/${slug}`,
          'isPartOf': { '@type': 'WebSite', 'name': '韩漫网站', 'url': 'https://www.manhwa-site.com' },
        })}} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.manhwa-site.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': category.name, 'item': `https://www.manhwa-site.com/category/${slug}` },
          ],
        })}} />
      </main>

      <Footer />
    </>
  );
}